import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Smartphone, Code2, Globe, Share2, TrendingUp, Rocket, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const ICON_MAP = { Smartphone, Code2, Globe, Share2, TrendingUp, Rocket };

const services = [
  { id: "mobile-app-dev", title: "Mobile App Development", icon: "Smartphone", desc: "Native iOS/Android & Cross-platform Flutter/React Native solutions with ultra-fluid 60fps UI/UX.", metrics: "50+ Apps Deployed", details: ["iOS & Android native apps", "Flutter & React Native cross-platform", "App Store optimization & deployment", "Push notifications & analytics integration"] },
  { id: "custom-software-dev", title: "Custom Software Engineering", icon: "Code2", desc: "Scalable cloud-native architectures, enterprise microservices, API engineering, and automated CI/CD pipelines.", metrics: "99.99% Uptime SLA", details: ["Enterprise ERP & CRM systems", "Cloud-native microservices (AWS/GCP)", "API design & third-party integrations", "Automated CI/CD pipelines"] },
  { id: "web-dev", title: "High-Performance Web Development", icon: "Globe", desc: "Award-worthy Jamstack, Next.js, and React web applications engineered for lightning SEO and conversions.", metrics: "<0.8s Page Load", details: ["Full-stack React, Next.js, Node.js", "E-commerce platforms & SaaS products", "Progressive Web Apps (PWA)", "Performance optimization & SEO"] },
  { id: "social-media-marketing", title: "Social Media Marketing", icon: "Share2", desc: "Data-driven viral content campaigns, community building, influencer outreach, and brand positioning.", metrics: "+4.5M Impressions", details: ["Paid ad campaigns (Meta, Google, LinkedIn)", "Organic growth & content strategy", "Influencer partnerships & outreach", "Analytics, A/B testing & ROI tracking"] },
  { id: "business-development", title: "Strategic Business Development", icon: "TrendingUp", desc: "B2B growth strategies, funnel optimization, market expansion, and strategic corporate partnership alignment.", metrics: "+340% Revenue Boost", details: ["Lead pipeline orchestration", "B2B partnership & channel strategy", "Market research & competitive analysis", "Sales funnel design & CRO"] },
  { id: "product-marketing", title: "GTM Product Marketing", icon: "Rocket", desc: "End-to-end Go-To-Market launches, positioning statements, user acquisition strategies, and ad performance tuning.", metrics: "15x CAC to LTV", details: ["Go-To-Market strategy & launch plans", "Brand positioning & messaging", "User acquisition & retention campaigns", "Lifecycle marketing & automation"] },
];

export default function OurServices() {
  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Badge variant="outline" className="mb-4 border-[#0EA5E9]/30 text-[#0EA5E9] bg-[#0EA5E9]/5 px-3 py-1 text-xs tracking-wider uppercase">
            What We Do
          </Badge>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-50 mb-4">
            Our <span className="gradient-text">Services</span>
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
            Six core pillars of digital excellence — from engineering to growth marketing.
          </p>
        </motion.div>

        <div className="space-y-6">
          {services.map((svc, i) => {
            const Icon = ICON_MAP[svc.icon];
            return (
              <motion.div
                key={svc.id}
                data-testid={`service-detail-${svc.id}`}
                className="bg-[#0F172A] border border-white/[0.06] rounded-xl p-6 sm:p-8 hover:border-[#00F2FE]/20 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.08 }}
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#00F2FE]/10 to-[#3B82F6]/10 border border-[#00F2FE]/20 flex items-center justify-center shrink-0">
                    <Icon className="h-6 w-6 text-[#00F2FE]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                      <h2 className="font-heading font-bold text-lg sm:text-xl text-slate-100">{svc.title}</h2>
                      <span className="flex items-center gap-1.5 text-emerald-400 text-xs font-mono">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        {svc.metrics}
                      </span>
                    </div>
                    <p className="text-slate-400 text-sm leading-relaxed mb-4">{svc.desc}</p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {svc.details.map((d) => (
                        <li key={d} className="flex items-center gap-2 text-slate-500 text-sm">
                          <span className="w-1 h-1 rounded-full bg-[#0EA5E9]" />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div className="text-center mt-16" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-100 mb-4">
            Ready to get started?
          </h2>
          <Link to="/quote">
            <Button size="lg" className="bg-gradient-to-r from-[#00F2FE] via-[#0EA5E9] to-[#3B82F6] text-white font-bold hover:opacity-90 px-8 py-6 text-base gap-2">
              Get Instant Quote <ArrowRight className="h-5 w-5" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
